import { ThemeProvider } from "@aws-amplify/ui-react"
import type { CSSProperties } from "react"
import type { Branding } from "../schemata/biometry/Branding.js"
import style from "../style/Page.module.css"
import BiometryTheme from "../style/theme/BiometryTheme.js"
import AccentPalette from "../support/branding/AccentPalette.js"

type Properties = Readonly<{
	branding: Branding | undefined
	children: React.ReactNode
	isBrandingSettled: boolean
}>

/**
 * @description
 *	Declares the custom properties of the integration and renders the theme
 *	below them.
 *
 * @remarks
 *	The theme provider renders its own element without accepting a class name
 *	nor a style, and emits its tokens as custom properties on that very
 *	element: a var() written inside a token only resolves when the variable is
 *	declared on that element or on one of its ancestors. Hence this wrapper,
 *	which is also the element that keeps the chain of heights alive.
 *
 *	The 3 (three) stops of the Sanii gradient are collapsed into the accent of
 *	the integration, because they paint the progress bar of the Amplify widget
 *	during the camera stage.
 *
 *	Nothing is declared until the branding settles, because "not known yet"
 *	and "has no branding" would otherwise be the same state and both would
 *	paint the accent of Sanii, which every consumer would then be seen
 *	transitioning away from. Holding the declaration back covers all of them
 *	at once, so each piece only has to deal with its own visibility.
 *
 *	The 3 (three) stops of the gradient keep resolving while the declaration
 *	is held back, because Global.css also declares them on the root with the
 *	ones of Sanii. Their only consumer is the progress bar of the camera
 *	stage, which is not reached without the button that waits.
 */
export default function BrandedThemeProvider({ branding, children, isBrandingSettled }: Properties) {
	const palette = AccentPalette(branding?.accentColor ?? undefined)
	const variables = {
		"--sanii-accent": palette.accent,
		"--sanii-accent-soft": palette.accentSoft,
		"--sanii-gradient-end": palette.accent,
		"--sanii-gradient-middle": palette.accent,
		"--sanii-gradient-start": palette.accent,
		"--sanii-on-accent": palette.onAccent
	} as CSSProperties
	return (
		<div className={style["page-branding"]} style={isBrandingSettled ? variables : undefined}>
			<ThemeProvider theme={BiometryTheme}>{children}</ThemeProvider>
		</div>
	)
}
